import { Button, Icon, Tooltip } from '@lightspeed/design-system-react'
import { ApiRetailerPaymentType } from '@vend/types'
import React, { FunctionComponent } from 'react'
import { T, useT } from '@transifex/react'
import { useLocalizedPaymentTypeName } from '@vend/react-utilities'
import { useAppSelector } from '../../../state/hooks'
import { selectOutstandingBalance } from '../../../state/slices/currentSale/currentSaleSlice'
import { SsboTourStep } from '../ssbo-guided-tour/SsboTourStep'
import { useLSPayEntryPaymentActions } from './useLSPayEntryPaymentActions'

export interface Props {
  paymentType?: ApiRetailerPaymentType
  disabled?: boolean
  pay: (
    payment: ApiRetailerPaymentType,
    paymentData?: { lspayFlow: 'cnp' }
  ) => void
  amountTendered?: number | string
  paymentModalOpen?: boolean
}

export const LSPayButton: FunctionComponent<Props> = ({
  paymentType,
  disabled,
  pay,
  amountTendered,
  paymentModalOpen,
}) => {
  const localizedPaymentTypeName = useLocalizedPaymentTypeName()
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const t = useT()
  const {
    currencySymbol,
    handleTerminalPayment,
    isAmountBelowMinimum,
    isDisabled,
    isOffline,
    minimumAmount,
  } = useLSPayEntryPaymentActions({
    paymentType,
    disabled,
    pay,
    amountTendered,
  })

  if (!paymentType) {
    return
  }

  const tooltipMessage = isAmountBelowMinimum ? (
    <T
      _str="Payment amount must be at least {minAmount}"
      _context="tooltip message for lightspeed payments minimum amount"
      minAmount={`${currencySymbol}${minimumAmount.toFixed(2)}`}
      lineBreak={<br />}
    />
  ) : (
    <T
      _str="You need to be connected to the internet to take payments using this payment type."
      _context="tooltip message for lightspeed payments when offline"
      lineBreak={<br />}
    />
  )

  const tooltipHeader = isAmountBelowMinimum
    ? t('Minimum payment amount required', {
        _context: 'tooltip header for lightspeed payments minimum amount',
      })
    : t('You can’t use this payment type while offline.', {
        _context: 'tooltip message for lightspeed payments when offline',
      })

  return (
    <SsboTourStep stepId="pay" enabled={!isDisabled && !paymentModalOpen}>
      <Tooltip
        className="wr-tooltip"
        direction="center"
        message={tooltipMessage}
        header={tooltipHeader}
        canBeShown={isOffline || isAmountBelowMinimum}
        icon="info-circle"
      >
        <Button
          onClick={handleTerminalPayment}
          variant={outstandingBalance < 0 ? 'no' : 'go'}
          className="vd-btn vd-btn--jumbo vd-pt5 vd-pb5"
          data-hotkey-tip="1"
          disabled={isDisabled}
          data-track={`payment-type-${paymentType.name}`}
          data-id={paymentType.type_id}
        >
          <Icon icon="credit-card" />
          <span className="vd-ml2">
            {localizedPaymentTypeName(paymentType.name)}
          </span>
        </Button>
      </Tooltip>
    </SsboTourStep>
  )
}
